<template>
  <div class="order-analysis">
    <div class="order-analysis__left" :class="{ 'is-app': isApp }">
      <div v-if="!isApp" class="order-analysis__left-top">
        <div class="left">
          <img alt="" :src="avatar" />
        </div>
        <div class="center">
          <div class="top">{{ showTitle }}</div>
          <div class="bottom">{{ showDesc }}</div>
        </div>
        <div v-if="analyzeResult" class="tips-btn" :class="{ 'is-stream': isStreaming }">
          <div class="tips-btn__regen" @click="handleCreate(detailType)">
            <i aria-hidden="true" class="icon-refresh-line t-iconfont"></i>
            <span>{{ isStreaming ? '生成中...' : '重新生成' }}</span>
          </div>
        </div>
      </div>
      <div v-else-if="isApp && analyzeResult" class="app-btn" :class="{ 'is-stream': isStreaming }">
        <div class="app-btn__regen" @click="handleCreate(detailType)">
          <i aria-hidden="true" class="icon-loop-left-line t-iconfont"></i>
          <span>{{ isStreaming ? '生成中...' : '重新生成' }}</span>
        </div>
      </div>
      <div v-if="newFlowUpNumber && !isStreaming" class="order-analysis__left-tips">
        <div class="tips-banner">
          <div aria-hidden="true" class="tips-banner__icon">
            <i class="colorful-text icon-lightbulb-line t-iconfont"></i>
          </div>
          <div class="tips-banner__text">
            检测到新增 {{ newFlowUpNumber }} 次跟进，建议点击「重新生成」分析报告以获取最新洞察
          </div>
        </div>
      </div>
      <div class="order-analysis__left-content">
        <NewViewEmpty v-if="isEmpty" :type="detailType" @create="handleCreate"></NewViewEmpty>
        <NewViewLoading
          v-if="inAnalyseId"
          ref="loadingRef"
          :class="{ 'is-result': analyzeResult }"
        ></NewViewLoading>
        <div v-if="analyzeResult" class="analyze-result">
          <MarkdownView :show-string="analyzeResult" />
        </div>
      </div>
    </div>
    <div v-if="!isApp" class="order-analysis__right">
      <div class="input-area">
        <template v-if="!inputActive">
          <i class="icon-edit-line t-iconfont"></i>
          <span class="text" @click="handleInputClick">填写跟进内容</span>
        </template>
        <div v-else class="input-active">
          <el-input
            ref="textareaRef"
            v-model="memo"
            maxlength="1000"
            placeholder="填写跟进内容"
            :rows="4"
            show-word-limit
            type="textarea"
          />
          <div class="btn">
            <el-button @click="handleCancelClick">取消</el-button>
            <el-button :loading="addLoading" type="primary" @click="handleSendClick"
              >发送</el-button
            >
          </div>
        </div>
      </div>
      <div v-if="communicatePlanShowTag" class="list-area">
        <div class="list-area__top">
          <div class="title">访客计划</div>
          <div class="btn" @click="handleAddClick">+ 新建</div>
        </div>
        <div class="list-area__content">
          <template v-for="(plan, index) in planList" :key="plan.id">
            <div
              v-if="shouldShowCardTime(plan, index)"
              class="time"
              :class="{ 'time--with-border': index !== 0 }"
            >
              {{ cardTime(plan.executeTime) }}
            </div>
            <PlanListCard :plan-card="plan" />
          </template>
          <div v-if="page < pageTotal" class="load-more" @click="handleLoadMoreClick">加载更多</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, computed, onMounted, onBeforeUnmount } from 'vue'

import avatar from '@/assets/ai-avatar/ai-avatar-2x-9.png'
import NewViewEmpty from './components/new-view-empty.vue'
import NewViewLoading from './components/new-view-loading.vue'
import PlanListCard from './components/plan-list-card.vue'
import MarkdownView from '@/components/markdown-view/index.vue'
import xbb from '@xbb/xbb-utils'
import utils, { isMobile } from '@/utils'
import { ElMessage } from 'element-plus'
import xbbSdk from 'xbb-sdk'
import { getCustomRecordInfo } from '@/api'
import { getSseMsg } from '@/api/index.js'
import { createBusinessObjectReport } from '@/api/chat'

import { getCommunicatePlanList, addCustomerCommunicate } from '@/api/ai-detail'

const props = defineProps({
  saleOrderData: {
    type: Object,
    default: () => ({})
  },
  detailType: {
    type: String,
    default: ''
  },
  deatilData: {
    type: Object,
    default: () => ({})
  },
  isApp: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['success', 'refresh'])

const inputActive = ref(false)
const memo = ref('')
const addLoading = ref(false)
const textareaRef = ref(null)

const loading = ref(false)
const page = ref(1)
const pageSize = ref(10)
const pageTotal = ref(0)
const planList = ref([])
const sendXbbParams = ref({})
const isStreaming = ref(false) // 是否正在流式传输数据
const loadingRef = ref(null) // 加载组件实例
const isError = ref(false) // 是否错误状态
const isUnmounted = ref(false)

const analyzeResult = computed(() => props.saleOrderData.analyzeResult) // 分析结果
const lastUpdateTime = computed(() => props.saleOrderData.lastUpdateTime) // 最后更新时间
const communicatePlanShowTag = computed(() => props.saleOrderData.communicatePlanShowTag) // 是否显示访客计划
const inAnalyseId = computed(() => props.saleOrderData.inAnalyseId || isStreaming.value) // 正在分析的报告id
const newFlowUpNumber = computed(() => props.saleOrderData.newFlowUpNumber) // 新增跟进次数

const isEmpty = computed(() => {
  return !analyzeResult.value && !inAnalyseId.value
})

const showTitle = computed(() => {
  if (inAnalyseId.value) {
    return 'AI 分析师深度思考中...'
  } else if (analyzeResult.value) {
    return 'AI 分析师已为你生成'
  } else {
    return 'AI 分析师随时准备为你分析'
  }
})

const businessEnum = {
  opportunity: {
    idKey: 'opportunityId',
    desc: 'AI 帮你抓准商机'
  },
  customer: {
    idKey: 'customerId',
    desc: 'AI 帮你复盘客户'
  },
  clue: {
    idKey: 'clueId',
    desc: 'AI 帮你吃透线索'
  }
}

const showDesc = computed(() => {
  if (inAnalyseId.value) {
    return '预计 5min 内完成，可稍后再来查看，无需在此等候'
  } else if (analyzeResult.value && lastUpdateTime.value) {
    return '上次更新时间：' + xbb.formatTime(lastUpdateTime.value, 'yyyy-MM-dd HH:mm:ss')
  } else {
    return businessEnum[props.detailType].desc
  }
})

const cardTime = (time) => {
  return xbb.formatTime(time, 'yyyy-MM-dd')
}

const shouldShowCardTime = (plan, index) => {
  if (index === 0) {
    return true
  }

  return cardTime(plan.executeTime) !== cardTime(planList.value[index - 1]?.executeTime)
}

const handleInputClick = async () => {
  memo.value = ''
  inputActive.value = true
  await nextTick()
  textareaRef.value.focus()
}

const handleCancelClick = () => {
  inputActive.value = false
}

const handleSendClick = () => {
  if (!memo.value.trim()) {
    ElMessage.warning('请输入跟进内容')
    return
  }
  addLoading.value = true
  const business = businessEnum[props.detailType]
  addCustomerCommunicate({
    [business.idKey]: props.deatilData[business.idKey],
    memo: memo.value
  })
    .then((res) => {
      ElMessage.success('新增跟进记录成功')
      inputActive.value = false
    })
    .finally(() => {
      addLoading.value = false
    })
}

const getPlanList = async () => {
  loading.value = true
  const business = businessEnum[props.detailType]
  const res = await getCommunicatePlanList({
    page: page.value,
    pageSize: pageSize.value,
    [business.idKey]: props.deatilData[business.idKey]
  })
  planList.value.push(...res.result.communicatePlanList)
  pageTotal.value = res.result.pageHelper.pageTotal
  loading.value = false
}

const handleLoadMoreClick = () => {
  page.value++
  getPlanList()
}

async function* sseStreamToIterator(stream) {
  const decoder = new TextDecoder()
  let buffer = ''
  const reader = stream.getReader()
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        // 处理剩余数据
        if (buffer.trim()) {
          yield processSSEMessage(buffer)
        }
        break
      }
      buffer += decoder.decode(value, { stream: true })
      while (true) {
        const boundaryIndex = buffer.indexOf('\n\n')
        if (boundaryIndex === -1) break

        const fullMessage = buffer.substring(0, boundaryIndex)
        buffer = buffer.substring(boundaryIndex + 2)

        yield processSSEMessage(fullMessage)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function processSSEMessage(rawMessage) {
  const lines = rawMessage.split('\n')
  const message = {}
  for (const line of lines) {
    if (line.startsWith('data:')) {
      message.data = (message.data || '') + line.substring(5).trim() + '\n'
    }
    // 处理其他字段
    else if (line.startsWith('event:')) {
      message.event = line.substring(6).trim()
    } else if (line.startsWith('id:')) {
      message.id = line.substring(3).trim()
    } else if (line.startsWith('retry:')) {
      message.retry = parseInt(line.substring(6).trim(), 10)
    }
  }
  if (message.data) {
    message.data = message.data.trimEnd()
  }
  return message
}

const handleCreate = async (type) => {
  if (isStreaming.value) {
    return
  }
  try {
    isStreaming.value = true
    isError.value = false
    const business = businessEnum[type]
    const params = {
      corpid: utils.xbbLocalStorageGet('corpid') || '',
      userId: utils.xbbLocalStorageGet('userId') || '',
      platform: isMobile() ? 'dingtalk' : 'web',
      frontDev: xbb.isDevEnv() ? '1' : undefined
    }
    params[business.idKey] = props.deatilData[business.idKey]
    if (props.detailType === 'clue' && !props.deatilData.clueId) {
      params.clueId = props.deatilData.customerId
    }
    const stream = await createBusinessObjectReport(params)
    if (isUnmounted.value || !loadingRef.value) {
      return
    }

    loadingRef.value.mockData[0].type = 2
    // let content = ''
    for await (let { data: message } of sseStreamToIterator(stream)) {
      if (isUnmounted.value || !loadingRef.value) {
        break
      }
      if (message.startsWith('data:')) {
        message = message.replace('data:', '')
        // console.log('message1: ', message)
      }
      if (message.includes('DONE')) {
        loadingRef.value.mockData[0].type = 1
        loadingRef.value.mockData[1].type = 1
        loadingRef.value.mockData[2].type = 1
        isStreaming.value = false
        emits('success')
        break
      }
      if (isError.value) {
        ElMessage.error(message)
        isStreaming.value = false
        emits('refresh')
        break
      }
      if (message.includes('ERROR')) {
        isError.value = true
      }
      if (!isError.value) {
        const completion = JSON.parse(message)
        if (completion.orderProcessAnalyseStart === 1) {
          loadingRef.value.mockData[0].type = 1
          loadingRef.value.mockData[1].type = 2
          loadingRef.value.mockData[2].type = 3
        } else if (
          completion.orderProcessAnalyseThinkCost ||
          completion.orderProcessAnalyseThinkCost === 0
        ) {
          loadingRef.value.mockData[0].type = 1
          loadingRef.value.mockData[1].type = 1
          loadingRef.value.mockData[2].type = 2
        }
        // console.log('completion: ', completion)
      }
    }
  } catch (error) {
    isStreaming.value = false
    ElMessage.error(error?.message)
    emits('refresh')
  } finally {
    if (!isUnmounted.value) {
      isStreaming.value = false
    }
  }
}

const getParams = async (type) => {
  const res = await getCustomRecordInfo({ businessType: type })
  const { appId, menuId, formId, businessType, subBusinessType } = res.result
  return { appId, menuId, formId, businessType, subBusinessType }
}

const xbbIframeWeb = (e) => {
  if (e.data?.type === 'aiSuccessWeb') {
    handleXbbSuccessWeb(e)
  }
}

/**
 * 处理 XBB iframe 成功消息（Web端）
 * 校验是否为当前表单，并根据业务类型处理客户沟通记录或客户创建成功
 * @param {Object} e - 消息事件对象
 */
const handleXbbSuccessWeb = (e) => {
  const params = e.data.data
  const result = e.data.result
  const isSameForm =
    params.appId === sendXbbParams.value.appId &&
    params.menuId === sendXbbParams.value.menuId &&
    params.formId === sendXbbParams.value.formId &&
    params.businessType === sendXbbParams.value.businessType
  if (!isSameForm) return
  console.log('收到 XBB 消息：', result)
  page.value = 1
  planList.value = []
  getPlanList()
}

const handleAddClick = async () => {
  const params = await getParams(601)
  sendXbbParams.value = {
    appId: params.appId,
    menuId: params.menuId,
    formId: params.formId,
    saasMark: 1,
    businessType: params.businessType,
    subBusinessType: params.subBusinessType
  }
  const business = businessEnum[props.detailType]
  xbbSdk.sendMessage('addNewForm', {
    appId: params.appId,
    menuId: params.menuId,
    formId: params.formId,
    saasMark: 1,
    businessType: params.businessType,
    subBusinessType: params.subBusinessType,
    [business.idKey]: props.deatilData[business.idKey]
  })
}

onMounted(() => {
  isUnmounted.value = false
  if (props.saleOrderData.communicatePlanShowTag) {
    getPlanList()
  }
  if (props.saleOrderData.inAnalyseId) {
    if (props.saleOrderData.inAnalyseContent) {
      if (props.saleOrderData.inAnalyseContent.orderProcessAnalyseStart) {
        loadingRef.value.mockData[0].type = 1
        loadingRef.value.mockData[1].type = 2
        loadingRef.value.mockData[2].type = 3
      }
      if (
        props.saleOrderData.inAnalyseContent.orderProcessAnalyseThinkCost ||
        props.saleOrderData.inAnalyseContent.orderProcessAnalyseThinkCost === 0
      ) {
        loadingRef.value.mockData[0].type = 1
        loadingRef.value.mockData[1].type = 1
        loadingRef.value.mockData[2].type = 2
      }
    }
    getSseMsg({ chatRecordId: props.saleOrderData.inAnalyseId, type: 2 })
      .finally(() => {
        if (isUnmounted.value || !loadingRef.value) {
          return
        }
        loadingRef.value.mockData[0].type = 1
        loadingRef.value.mockData[1].type = 1
        loadingRef.value.mockData[2].type = 1
        emits('success')
      })
      .catch((err) => {
        console.log(err)
      })
  }
  window.addEventListener('message', xbbIframeWeb)
})

onBeforeUnmount(() => {
  isUnmounted.value = true
  window.removeEventListener('message', xbbIframeWeb)
})

defineExpose({
  showTitle,
  showDesc
})
</script>

<style lang="scss" scoped>
.order-analysis {
  --order-analysis-tips-bg: linear-gradient(273deg, #eef2ff 1%, #f5f3ff 100%);
  --order-analysis-tips-border: #f5e8ff;
  --order-analysis-tips-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.05);
  --order-analysis-tips-text: #323233;
  flex: 1;
  position: relative;
  overflow: hidden;
  display: flex;
  .order-analysis__left {
    flex: 1;
    padding: 16px;
    border-radius: 12px;
    background: #ffffff;
    overflow: auto;
    display: flex;
    flex-direction: column;
    .order-analysis__left-top {
      flex: 0 0 auto;
      display: flex;
      margin-bottom: 16px;
      .left {
        height: 40px;
        width: 40px;
        margin-right: 12px;
        position: relative;
        img {
          height: 100%;
          width: 100%;
          border-radius: 50%;
        }
      }
      .center {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        .top {
          font-size: 16px;
          font-weight: 600;
          color: #323233;
        }
        .bottom {
          margin-top: 2px;
          font-size: 13px;
          line-height: 18px;
          color: #646566;
        }
      }

      .tips-btn {
        flex: 0 0 auto;
        margin-left: 12px;
        display: flex;
        align-items: center;
        .tips-btn__regen {
          height: 24px;
          border-radius: 8px;
          padding: 4px 8px;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          i {
            font-size: 14px;
            color: #646566;
          }
          span {
            margin-left: 4px;
            font-size: 12px;
            line-height: 16px;
            color: #646566;
          }
        }
      }
      .is-stream {
        .tips-btn__regen {
          cursor: not-allowed;
          i {
            font-size: 14px;
            color: #c8c9cc;
          }
          span {
            margin-left: 4px;
            font-size: 12px;
            line-height: 16px;
            color: #c8c9cc;
          }
        }
      }
    }
    .app-btn {
      flex: 0 0 auto;
      margin-bottom: 16px;
      .app-btn__regen {
        width: 100%;
        height: 32px;
        border-radius: 8px;
        padding: 8px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        background: #f7f8fa;
        i {
          font-size: 14px;
          color: #646566;
        }
        span {
          margin-left: 4px;
          font-size: 12px;
          line-height: 16px;
          color: #646566;
        }
      }
    }
    .is-stream {
      .app-btn__regen {
        background: #f7f8fa;
        i {
          color: #c8c9cc;
        }
        span {
          color: #c8c9cc;
        }
      }
    }
    .order-analysis__left-tips {
      flex: 0 0 auto;
      margin-bottom: 16px;
      display: flex;
      flex-direction: column;
    }
    .order-analysis__left-content {
      flex: 1;
      border-radius: 24px;
      overflow: auto;
      display: flex;
      flex-direction: column;
      .analyze-result {
        border-radius: 24px;
        padding: 16px;
        background: #f7f8fa;
        flex: 1;
      }
      .is-result {
        height: 196px;
        margin-bottom: 16px;
      }
    }
    .tips-banner {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      min-height: 42px;
      padding: 12px;
      border: 1px solid var(--order-analysis-tips-border);
      border-radius: 12px;
      background: var(--order-analysis-tips-bg);
      box-shadow: var(--order-analysis-tips-shadow);
      box-sizing: border-box;
      &__icon {
        flex: 0 0 18px;
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        i {
          font-size: 18px;
        }
      }
      &__text {
        flex: 1;
        font-size: 13px;
        line-height: 18px;
        color: var(--order-analysis-tips-text);
      }
    }
  }
  .is-app {
    border-radius: unset;
    padding: 12px;
  }
  .order-analysis__right {
    flex: 0 0 468px;
    margin-left: 16px;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    .input-area {
      background: #ffffff;
      border-radius: 12px;
      padding: 12px 16px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      i {
        margin-right: 8px;
        font-size: 16px;
        color: #969799;
      }
      .text {
        font-size: 14px;
        line-height: 20px;
        color: #c8c9cc;
      }
      .input-active {
        flex: 1;
        :deep(.el-textarea__inner:focus) {
          box-shadow: 0 0 0 1px #7a52f4;
        }
        .btn {
          display: flex;
          justify-content: end;
          margin-top: 12px;
          :deep(.el-button) {
            border-radius: 8px;
          }
        }
      }
    }
    .list-area {
      background: #ffffff;
      border-radius: 12px;
      padding: 16px 16px 4px 16px;
      display: flex;
      flex-direction: column;
      .list-area__top {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;
        .title {
          font-size: 16px;
          font-weight: 600;
          line-height: 22px;
          color: #323233;
        }
        .btn {
          font-size: 14px;
          line-height: 20px;
          color: #248df5;
          cursor: pointer;
        }
      }
      .list-area__content {
        display: flex;
        flex-direction: column;
        .time {
          font-size: 14px;
          line-height: 20px;
          margin-bottom: 12px;
          color: #969799;
          &--with-border {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px dashed #f2f3f5;
          }
        }
        .load-more {
          margin-bottom: 12px;
          font-size: 14px;
          line-height: 20px;
          color: #248df5;
          text-align: center;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
